import type {
	ImageAndTextUmbBlockElementModel,
	ImageAndTextUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { ImageAndText } from "#/_packages/umbrella/features/image-and-text/components";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import {
	toHoverItemCssClasses,
	toHoverMediaCssClasses,
} from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils.ts";
import {
	getMediaAlt,
	getResponsiveImageProps,
} from "#/_packages/umbrella/integrations/umbraco/utils/image-utils.ts";
import { getUmbBlockListLinks } from "#/_packages/umbrella/integrations/umbraco/utils/link-utils.ts";
import type { ImageMedia } from "#/_packages/umbrella/types";
import {
	tryGetAspectRatio,
	tryGetHeadingLevel,
} from "#/_packages/umbrella/utils";
import { useCmsImageOptions } from "../../utils/media-composition";

type ImageAndTextBlockProps = BlockItemProps & {
	content: ImageAndTextUmbBlockElementModel;
	settings: ImageAndTextUmbBlockSettingsElementModel;
};

export default function ImageAndTextUmbBlock({
	className,
	content,
	settings,
	mediaPriority,
	mediaSizes,
}: ImageAndTextBlockProps) {
	const rawImage = content.properties?.image?.[0];
	const settingsProperties = settings.properties;

	const links = getUmbBlockListLinks(content.properties?.links?.items);
	const mediaOptions = useCmsImageOptions(
		content.id,
		"half",
		mediaPriority,
		mediaSizes,
	);
	const image: ImageMedia | undefined = rawImage
		? {
				...getResponsiveImageProps(rawImage, {
					aspectRatio: tryGetAspectRatio(
						content.properties?.aspectRatio,
						"16:9",
					),
					...mediaOptions,
				}),
				alt: getMediaAlt(rawImage),
				caption: content.properties?.imageCaption ?? undefined,
			}
		: undefined;

	return (
		<ImageAndText
			id={settingsProperties?.generatedId ?? undefined}
			className={className}
			image={image}
			imageAlignLeft={
				content.properties?.imageAlignLeft
					? content.properties.imageAlignLeft
					: false
			}
			aspectRatio={tryGetAspectRatio(content.properties?.aspectRatio, "16:9")}
			headingLabel={content.properties?.headingLabel ?? undefined}
			heading={content.properties?.heading ?? undefined}
			headingLevel={tryGetHeadingLevel(content.properties?.headingLevel)}
			headingAppearance={content.properties?.headingAppearance}
			preamble={content.properties?.preamble ?? undefined}
			body={
				content.properties?.body?.markup ? (
					<div
						className="rich-text"
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Rich text markup comes from Umbraco CMS.
						dangerouslySetInnerHTML={{
							__html: content.properties.body.markup,
						}}
					/>
				) : undefined
			}
			links={links}
			animationScrollType={settingsProperties?.animationScrollType}
			hoverItemClasses={toHoverItemCssClasses(
				settingsProperties?.animationHoverType,
			)}
			hoverMediaClasses={toHoverMediaCssClasses(
				settingsProperties?.animationHoverType,
			)}
		/>
	);
}
